import { useState, useEffect } from "react";

const CHAT_HISTORY_KEY = "writing_assistant_chat_history_admin";

/**
 * Custom hook to manage writing assistant chat history for the admin panel.
 * Persists to localStorage, scoped to admin with a unique key.
 */
export const useChatHistory = () => {
  const [chatHistories, setChatHistories] = useState([]);

  useEffect(() => {
    loadChatHistories();
  }, []);

  const loadChatHistories = () => {
    try {
      const stored = localStorage.getItem(CHAT_HISTORY_KEY);
      if (stored) {
        const histories = JSON.parse(stored);
        const sorted = histories.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        setChatHistories(sorted);
      }
    } catch {
      setChatHistories([]);
    }
  };

  const saveChatHistory = (chatData, formData = {}) => {
    try {
      const histories = [...chatHistories];
      const firstPrompt = chatData.find((item) => item.type === "prompt");
      let title = "New Chat";
      if (formData.title?.trim()) {
        title = formData.title.trim().substring(0, 40);
        if (formData.title.length > 40) title += "...";
      } else if (firstPrompt?.chatText) {
        const t = firstPrompt.chatText.trim();
        title = t.substring(0, 40) + (t.length > 40 ? "..." : "");
      }
      const newHistory = {
        id: Date.now().toString(),
        title,
        chatData,
        formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      histories.unshift(newHistory);
      const limited = histories.slice(0, 50);
      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(limited));
      setChatHistories(limited);
      return newHistory;
    } catch {
      return null;
    }
  };

  const updateChatHistory = (id, chatData, formData = {}) => {
    try {
      const histories = chatHistories.map((h) => {
        if (h.id !== id) return h;
        const firstPrompt = chatData.find((item) => item.type === "prompt");
        let title = h.title || "New Chat";
        if (formData.title?.trim()) {
          title = formData.title.trim().substring(0, 40);
          if (formData.title.length > 40) title += "...";
        } else if (firstPrompt?.chatText && !h.title) {
          const t = firstPrompt.chatText.trim();
          title = t.substring(0, 40) + (t.length > 40 ? "..." : "");
        }
        return { ...h, chatData, formData, title, updatedAt: new Date().toISOString() };
      });
      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(histories));
      setChatHistories(histories);
      return true;
    } catch {
      return false;
    }
  };

  const deleteChatHistory = (id) => {
    try {
      const filtered = chatHistories.filter((h) => h.id !== id);
      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(filtered));
      setChatHistories(filtered);
      return true;
    } catch {
      return false;
    }
  };

  const clearAllChatHistories = () => {
    try {
      localStorage.removeItem(CHAT_HISTORY_KEY);
      setChatHistories([]);
      return true;
    } catch {
      return false;
    }
  };

  const getChatHistory = (id) => chatHistories.find((h) => h.id === id);

  return {
    chatHistories,
    saveChatHistory,
    updateChatHistory,
    deleteChatHistory,
    clearAllChatHistories,
    getChatHistory,
    loadChatHistories,
  };
};
